<script>
import Layout from '@layouts/adminLayout.vue'
import LoaderIcon from '@components/loader-icon.vue'
import TabContainer from '@components/tab-container.vue'
import ReferralsTable from '@components/network/referrals-table.vue'
import { isMobileMixin } from '@src/mixins/isMobileMixin'
import { authComputed } from '@state/helpers'
import {
  getMySentReferrals,
  getMyReceivedReferrals,
  getMyOfficeReferrals,
  getMyOfficeSentReferrals,
} from '@src/api/networkConnect'
import { arrayToSentence } from '@utils/string-helpers'
import router from '@router'
import { getOfficeReceivedStatus } from '@utils/network-connect-helpers'

export default {
  page: {
    title: 'Network Connect',
    meta: [
      {
        name: 'description',
        content: 'The Network Connect page.',
      },
    ],
  },
  components: {
    Layout,
    LoaderIcon,
    TabContainer,
    ReferralsTable,
  },
  mixins: [isMobileMixin],
  data() {
    return {
      loading: true,
      error: false,
      referralCategory: 'received',
      referralType: 'Users',
      receivedReferrals: [],
      sentReferrals: [],
      officeReferrals: [],
      tableFields: [],
      desktopReceivedTableFields: [
        {
          key: 'state',
          label: 'State',
          sortable: true,
        },
        {
          key: 'loanType',
          label: 'Loan Type',
          sortable: true,
        },
        {
          key: 'sender',
          label: 'Sender',
          sortable: true,
        },
        {
          key: 'loanAmount',
          label: 'Loan Amount',
          thStyle: { 'white-space': 'nowrap' },
          sortable: true,
        },
        {
          key: 'dateReceived',
          label: 'Date Received',
          thStyle: { 'white-space': 'nowrap' },
        },
        {
          key: 'receivedStatus',
          label: 'Status',
        },
      ],
      desktopOfficeReceivedTableFields: [
        {
          key: 'state',
          label: 'State',
          sortable: true,
        },
        {
          key: 'loanType',
          label: 'Loan Type',
          sortable: true,
        },
        {
          key: 'sender',
          label: 'Sender',
          sortable: true,
        },
        {
          key: 'loanAmount',
          label: 'Loan Amount',
          thStyle: { 'white-space': 'nowrap' },
          sortable: true,
        },
        {
          key: 'dateReceived',
          label: 'Date Received',
          thStyle: { 'white-space': 'nowrap' },
        },
        {
          key: 'assigned',
          label: 'Assigned',
        },
        {
          key: 'receivedStatus',
          label: 'Status',
        },
      ],
      desktopSentTableFields: [
        {
          key: 'state',
          label: 'State',
          sortable: true,
        },
        {
          key: 'loanType',
          label: 'Loan Type',
          sortable: true,
        },
        {
          key: 'recipients',
          label: 'Recipients',
          sortable: true,
        },
        {
          key: 'loanAmount',
          label: 'Loan Amount',
          thStyle: { 'white-space': 'nowrap' },
          sortable: true,
        },
        {
          key: 'dateSent',
          label: 'Date Sent',
          thStyle: { 'white-space': 'nowrap' },
        },
        {
          key: 'status',
          label: 'Status',
        },
      ],
      desktopOfficeSentTableFields: [
        {
          key: 'state',
          label: 'State',
          sortable: true,
        },
        {
          key: 'loanType',
          label: 'Loan Type',
          sortable: true,
        },
        {
          key: 'recipients',
          label: 'Recipients',
          sortable: true,
        },
        {
          key: 'loanAmount',
          label: 'Loan Amount',
          thStyle: { 'white-space': 'nowrap' },
          sortable: true,
        },
        {
          key: 'dateSent',
          label: 'Date Sent',
          thStyle: { 'white-space': 'nowrap' },
        },
        {
          key: 'officeSender',
          label: 'Sender',
          sortable: true,
        },
        {
          key: 'status',
          label: 'Status',
        },
      ],
      tableFieldsMobile: [
        {
          key: 'state',
          label: 'State',
          sortable: true,
        },
      ],
    }
  },
  computed: {
    ...authComputed,
    primaryOfficeTradeName() {
      return this.currentUser.primaryOffice?.tradeName || ''
    },
    tableData() {
      if (this.referralCategory === 'received') {
        return this.receivedReferrals.map((referral) => ({
          ...referral,
          dateReceived: referral.receivedAt,
          sender: referral.createdBy?.name,
        }))
      }

      if (this.referralCategory === 'office_received') {
        return this.officeReferrals.map((referral) => ({
          ...referral,
          dateReceived: referral.receivedAt,
          sender: referral.createdBy?.name,
          assigned: arrayToSentence(
            referral.officeUsers?.map((user) => user.name),
            ', ',
            ', '
          ),
          officeReceivedStatus: getOfficeReceivedStatus({
            referral,
            officeUsers: referral.officeUsers,
          }),
        }))
      }

      if (this.referralCategory === 'office_sent') {
        return this.officeSentReferrals.map((referral) => ({
          ...referral,
          dateSent: referral.sentDate,
          officeSender: referral.createdBy?.name,
          recipients: (() => {
            if (referral.referralType === 'Users') {
              const names = referral.users?.map((user) => user.name) || []
              return arrayToSentence(names, ', ', ', ')
            }

            if (
              referral.referralType === 'Offices' &&
              (referral.status === 'Pending Confirmation' || referral.status === 'Completed')
            ) {
              const names = referral.users?.map((user) => user.name) || []
              return arrayToSentence(names, ', ', ', ')
            }

            if (referral.referralType === 'Offices') {
              const tradeNames = referral.offices?.map((office) => office.tradeName) || []
              return arrayToSentence(tradeNames, ', ', ', ')
            }

            return referral.state
          })(),
        }))
      }

      return this.sentReferrals.map((referral) => ({
        ...referral,
        dateSent: referral.sentDate,
        recipients: (() => {
          if (referral.referralType === 'Users') {
            const names = referral.users?.map((user) => user.name) || []
            return arrayToSentence(names, ', ', ', ')
          }

          if (
            referral.referralType === 'Offices' &&
            (referral.status === 'Pending Confirmation' || referral.status === 'Completed')
          ) {
            const names = referral.users?.map((user) => user.name) || []
            return arrayToSentence(names, ', ', ', ')
          }

          if (referral.referralType === 'Offices') {
            const tradeNames = referral.offices?.map((office) => office.tradeName) || []
            return arrayToSentence(tradeNames, ', ', ', ')
          }

          return referral.state
        })(),
      }))
    },
    tableDataActive() {
      return this.tableData.filter((data) => data.active)
    },
    tableDataInactive() {
      return this.tableData.filter((data) => !data.active)
    },
    isUserLO() {
      const groups = this.currentUser?.groups?.map((x) => x.label) || []
      return groups.includes('Loan Originator')
    },
  },
  watch: {
    isMobile() {
      this.tableFields = this.getTableFields()
    },
    referralCategory() {
      this.tableFields = this.getTableFields()
      this.convertReferralTypeOnReferralCategory()
    },
  },

  created() {
    if (!this.canManageNetworkConnect) {
      router.push({ name: 'home' })
    }
    this.tableFields = this.getTableFields()
    if (this.isBoOrOpsManager) {
      this.referralType = 'Offices'
      this.referralCategory = 'office_received'
    }
    this.loadNetworkConnect()
  },

  methods: {
    async loadNetworkConnect() {
      try {
        this.loading = true
        let officeReferrals = []
        let officeSentReferrals = []
        const [
          {
            data: { data: receivedReferrals },
          },
          {
            data: { data: sentReferrals },
          },
          ,
        ] = await Promise.all([getMyReceivedReferrals(), getMySentReferrals()])

        if (this.isBoOrOpsManager) {
          const {
            data: { data: myOfficeReferrals },
          } = await getMyOfficeReferrals()
          officeReferrals = myOfficeReferrals

          const {
            data: { data: myOfficeSentReferrals },
          } = await getMyOfficeSentReferrals()
          officeSentReferrals = myOfficeSentReferrals
        }

        this.receivedReferrals = receivedReferrals.map((referral) => ({
          ...referral,
          referralCategory: 'received',
        }))
        this.sentReferrals = sentReferrals.map((referral) => ({
          ...referral,
          referralCategory: 'sent',
        }))
        this.officeReferrals = officeReferrals.map((referral) => ({
          ...referral,
          referralCategory: 'office_received',
        }))
        this.officeSentReferrals = officeSentReferrals.map((referral) => ({
          ...referral,
          referralCategory: 'office_sent',
        }))

        if (this.$route.params.selectedTab) {
          this.referralCategory = this.$route.params.selectedTab
        }
      } catch (e) {
        this.error = true
      } finally {
        this.loading = false
      }
    },

    getTableFields() {
      if (this.isMobile) {
        return this.tableFieldsMobile
      }

      if (this.referralCategory === 'sent') {
        return this.desktopSentTableFields
      }

      if (this.referralCategory === 'office_received') {
        return this.desktopOfficeReceivedTableFields
      }

      if (this.referralCategory === 'office_sent') {
        return this.desktopOfficeSentTableFields
      }

      return this.desktopReceivedTableFields
    },

    pickReferralType(referralType) {
      this.referralType = referralType
      this.convertReferralCategoryOnReferralType()
    },

    convertReferralCategoryOnReferralType() {
      if (this.referralType === 'Offices') {
        if (this.referralCategory === 'received') {
          this.referralCategory = 'office_received'
        }

        if (this.referralCategory === 'sent') {
          this.referralCategory = 'office_sent'
        }
      } else {
        if (this.referralCategory === 'office_received') {
          this.referralCategory = 'received'
        }

        if (this.referralCategory === 'office_sent') {
          this.referralCategory = 'sent'
        }
      }
    },

    convertReferralTypeOnReferralCategory() {
      if (this.referralCategory === 'received' || this.referralCategory === 'sent') {
        this.referralType = 'Users'
      } else {
        this.referralType = 'Offices'
      }
    },
  },
}
</script>

<template>
  <Layout>
    <LoaderIcon v-if="loading || error" :error="error" />
    <div v-else>
      <div class="d-flex align-items-center justify-content-between">
        <BaseTitle class="color-primary-blue">Network Connect</BaseTitle>
        <RouterLink :class="$style.newReferralButtonMobile" :to="{ name: 'network-connect-new-referral' }">
          <BaseButton :options="{ defaultPadding: true }">Create Referral</BaseButton>
        </RouterLink>
      </div>
      <div v-if="isBoOrOpsManager" :class="$style.referralTypeToggleContainer">
        <BaseSelector :active="referralType === 'Users'" class="mr-15" @click="pickReferralType('Users')">
          Individual
        </BaseSelector>
        <BaseSelector :active="referralType === 'Offices'" @click="pickReferralType('Offices')">Office</BaseSelector>
      </div>
      <TabContainer>
        <b-nav class="motto-tabs mt-4" tabs>
          <b-nav-item
            v-show="!isMobile || referralType !== 'Offices'"
            :active="referralCategory === 'received'"
            @click="referralCategory = 'received'">
            Received
          </b-nav-item>
          <b-nav-item
            v-show="!isMobile || referralType !== 'Offices'"
            :active="referralCategory === 'sent'"
            @click="referralCategory = 'sent'">
            Sent
          </b-nav-item>
          <b-nav-item
            v-show="!isMobile || referralType !== 'Users'"
            v-if="isBoOrOpsManager"
            :active="referralCategory === 'office_received'"
            @click="referralCategory = 'office_received'">
            {{ primaryOfficeTradeName }} - Received
          </b-nav-item>
          <b-nav-item
            v-show="!isMobile || referralType !== 'Users'"
            v-if="isBoOrOpsManager"
            :active="referralCategory === 'office_sent'"
            @click="referralCategory = 'office_sent'">
            {{ primaryOfficeTradeName }} - Sent
          </b-nav-item>
          <RouterLink :class="$style.newReferralButton" :to="{ name: 'network-connect-new-referral' }">
            <BaseButton :options="{ defaultPadding: true }">Create Referral</BaseButton>
          </RouterLink>
        </b-nav>
        <template v-slot:content>
          <template v-if="referralCategory === 'received' && !isUserLO">
            <p class="text-center color-dark-gray">You must be a licensed MLO to receive referrals here.</p>
          </template>
          <template v-else>
            <p v-if="!tableData.length" class="text-center color-dark-gray">No referrals found</p>
            <h2 v-if="tableDataActive.length" class="mt-2">Active Referrals</h2>
            <ReferralsTable :table-data="tableDataActive" :table-fields="tableFields" />
            <h2 v-if="tableDataInactive.length" class="mt-2">Inactive Referrals</h2>
            <ReferralsTable :table-data="tableDataInactive" :table-fields="tableFields" />
          </template>
        </template>
      </TabContainer>
    </div>
  </Layout>
</template>

<style lang="scss" module>
@import '@design';

.newReferralButton {
  @media (max-width: $lg-breakpoint) {
    display: none;
  }

  display: block;
  margin-top: -15px;
  margin-left: auto;
}

.newReferralButtonMobile {
  @media (max-width: $lg-breakpoint) {
    display: block;
    margin-left: 10px;
    white-space: nowrap;
  }

  display: none;
}

.referralTypeToggleContainer {
  @media (max-width: $lg-breakpoint) {
    display: flex;
    margin-top: 20px;
  }

  display: none;
}
</style>
